import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Divider } from "staysure-component-library";

import LabelButtonWrapper from "@/components/atoms/button-wrapper/button-wrapper";
import MoreInfoDrawer from "@/components/molecules/quote-section/acos/more-info-drawer";
import { PRODUCT, SPREAD_THE_COST } from "@/constants";
import { PriceSummaryDetailsSchemaType } from "@/schema/renewal-page/payment-summary-page.schema";
import { RootState } from "@/store/root-reducer";
import { OptionalExtra } from "@/types/OptionalExtraType";

import { RowItemPrice, TotalAmountRow } from "./price-show-row/price-show-row";
import { PromoVoucherSection } from "./promo-voucher-section/promo-voucher-section";

type PriceSummaryDetailsProps = {
  content: PriceSummaryDetailsSchemaType;
  gotAPromoOrVoucherCode?: () => void;
};

type PolicyPackage =
  | "BASIC"
  | "COMPREHENSIVE"
  | "SIGNATURE"
  | "ESSENTIALS"
  | "DELUXE"
  | "CLASSIC";

export function PriceSummaryDetails({
  content,
  gotAPromoOrVoucherCode,
}: Readonly<PriceSummaryDetailsProps>) {
  const {
    tripTypeText,
    coverLevel,
    totalAmountText,
    totalToPayTodayText,
    totalCreditChargeText,
    annualPercentageRateText,
    installmentsText,
    btnPromoOrVoucherLinkLabel,
    changeToCurrentPolicyText,
    moreInfoText,
    medicalText,
    renewalPolicyText,
    removeModal,
    removeCta,
    mtaMoreInfoDrawer,
  } = content;

  const [isOpen, setIsOpen] = useState(false);
  const { attributes } = useSelector((state: RootState) => state.quote);
  const payOption = useSelector((state: RootState) => state.paymentConsents);
  const { priceResult, tripDetails, paymentDetails, mtaMedicalConsent } =
    attributes;

  const isMonthly: boolean = payOption?.type === SPREAD_THE_COST;

  const isMta = mtaMedicalConsent?.isTravellingBeforeRenewal;
  const mtaPrice =
    paymentDetails?.paymentOptions[0]?.cardPayment?.card?.mtaPrice;
  const depositAmount =
    paymentDetails?.paymentOptions[0]?.cardPayment?.premiumCreditPayment[0]?.pcl
      ?.depositAmount;

  // get cover
  const cover = useMemo(() => {
    if (!tripDetails?.policyPackage)
      return {
        coverName: "",
        coverAmount: 0,
      };
    const covers: Record<PolicyPackage, string> = {
      BASIC: coverLevel.basic ?? "Basic",
      COMPREHENSIVE: coverLevel.comprehensive ?? "Comprehensive",
      SIGNATURE: coverLevel.signature ?? "Signature",
      ESSENTIALS: coverLevel.essentials ?? "Essential",
      DELUXE: coverLevel.deluxe ?? "Deluxe",
      CLASSIC: coverLevel.classic ?? "Classic",
    };

    let coverLevelName = covers[tripDetails.policyPackage as PolicyPackage];
    if (tripDetails?.goingOnCruise && PRODUCT === "avanti") {
      if (tripDetails?.policyPackage === "CLASSIC") {
        coverLevelName = coverLevel.classicCruise ?? "Classic Cruise";
      } else if (tripDetails?.policyPackage === "DELUXE") {
        coverLevelName = coverLevel.deluxeCruise ?? "Deluxe Cruise";
      }
    }

    const policyTypeAmountFilter =
      priceResult?.policyPackagePrice?.filter(
        (item) =>
          item?.policyPackage === tripDetails?.policyPackage &&
          item?.policyType === tripDetails?.policyType
      ) ?? [];

    return {
      coverName: coverLevelName,
      coverAmount: policyTypeAmountFilter[0].total,
    };
  }, [
    tripDetails?.policyPackage,
    tripDetails?.goingOnCruise,
    tripDetails?.policyType,
    priceResult?.policyPackagePrice,
    coverLevel,
  ]);

  // select optional extras
  const optionalExtras = useMemo(() => {
    return priceResult?.optionalExtras?.filter(
      (extra) => extra.price[0].selected
    );
  }, [priceResult?.optionalExtras]);

  return (
    <div className="flex flex-col mt-[-4px]">
      {/* MTA */}
      {isMta && (
        <div>
          <div
            className={` flex  flex-col md:justify-between md:flex-row  gap-spacing-xxs md:gap-0`}
          >
            <p className="text-dark-text-functional font-body-font text-xs font-bold leading-[150%] h-[12px] md:h-auto">
              {changeToCurrentPolicyText}
            </p>
            <div className="w-fit">
              <LabelButtonWrapper
                id="price-summary-details-more-info"
                label={moreInfoText ?? "More info"}
                hierarchy="tertiary"
                semanticColor="Dark Background"
                className={` font-body-font leading-[24px] font-[600] text-[16px] !px-0 !py-0`}
                onClick={() => {
                  setIsOpen(true);
                }}
              />
            </div>
          </div>
          <div className="md:mt-spacing-xxs mt-spacing-xs">
            {/* TO DO */}
            <RowItemPrice
              price={Number(
                paymentDetails?.paymentOptions[0]?.cardPayment?.card
                  ?.mtaPrice ?? "0.00"
              )}
              title={medicalText}
            />
          </div>
          <Divider
            thickness={1}
            className={`!mt-spacing-m ${
              isMta ? "!mb-spacing-m" : "!mb-spacing-l"
            } `}
            margin={0}
            color="#7D7D7D"
          />
          <div
            className={`${
              isMta
                ? "md:pt-spacing-l mb-spacing-xs lg:pt-spacing-m md:pb-spacing-m"
                : "pt-spacing-s pb-[20px]"
            }  `}
          >
            <p className="text-dark-text-functional font-body-font text-xs font-bold leading-[150%]">
              {renewalPolicyText}
            </p>
          </div>
        </div>
      )}

      {/* cover type */}
      <RowItemPrice
        price={cover.coverAmount}
        title={cover.coverName}
        description={tripTypeText}
      />

      <PromoVoucherSection removeCta={removeCta} removeModal={removeModal} />

      <Divider
        thickness={1}
        className="!my-spacing-l !mt-spacing-m"
        margin={0}
        color="#7D7D7D"
      />

      {/* optional extra items */}
      {optionalExtras && optionalExtras?.length > 0 ? (
        <>
          <div className="flex flex-col gap-spacing-l">
            {optionalExtras.map((extra: OptionalExtra) => {
              return (
                <RowItemPrice
                  key={extra.price[0].name}
                  price={extra.price[0].sellPrice}
                  title={extra.price[0].name}
                  isSemiBold
                />
              );
            })}
          </div>
          <Divider
            thickness={1}
            className="!mt-spacing-l !mb-spacing-m"
            margin={0}
            color="#7D7D7D"
          />
        </>
      ) : null}

      {/* Total amount */}
      <RowItemPrice
        price={
          isMonthly
            ? Number(
                paymentDetails?.paymentOptions[0]?.cardPayment
                  ?.premiumCreditPayment?.[0]?.pcl?.totalCost
              )
            : Number(
                paymentDetails?.paymentOptions[0]?.cardPayment?.card?.totalPrice
              )
        }
        title={totalAmountText}
      />
      <Divider
        thickness={1}
        className="!mt-spacing-m !mb-spacing-l"
        margin={0}
        color="#7D7D7D"
      />
      {isMonthly && (
        <>
          <div
            id="price-summary-details-pcl"
            className="flex flex-col gap-spacing-m"
          >
            {/* total pay today */}
            <TotalAmountRow
              price={Number(mtaPrice ?? 0) + Number(depositAmount)}
              title={totalToPayTodayText}
            />
            {/* total credit charge  */}
            <RowItemPrice
              price={Number(
                paymentDetails?.paymentOptions[0]?.cardPayment
                  ?.premiumCreditPayment[0]?.pcl?.costOfCredit
              )}
              title={totalCreditChargeText}
            />
            {/* annual percentage rate */}
            <RowItemPrice
              price={Number(
                paymentDetails?.paymentOptions[0]?.cardPayment
                  ?.premiumCreditPayment[0]?.pcl?.annualPercentageRate
              )}
              title={annualPercentageRateText}
              percentage="% APR"
            />
          </div>

          {/* installments */}
          <div className="mt-spacing-xs">
            <RowItemPrice
              price={Number(
                paymentDetails?.paymentOptions[0]?.cardPayment
                  ?.premiumCreditPayment[0]?.pcl?.collectionAmount
              )}
              title={installmentsText}
            />
          </div>
        </>
      )}
      <div
        className={`${
          isMonthly
            ? `${isMta ? "lg:mt-spacing-l" : "lg:mt-spacing-m"}`
            : "lg:mt-spacing-l"
        } md:mt-0 mt-spacing-s`}
      >
        <LabelButtonWrapper
          id="price-summary-details-more-info"
          label={btnPromoOrVoucherLinkLabel ?? "Got a promo or voucher code?"}
          hierarchy="tertiary"
          semanticColor="Dark Background"
          className={` font-body-font leading-[24px] font-[600] text-[16px] !px-0`}
          onClick={gotAPromoOrVoucherCode}
        />
      </div>

      <MoreInfoDrawer
        isDraweOpenOpen={isOpen}
        setIsDraweOpenOpen={(e) => setIsOpen(e)}
        title={mtaMoreInfoDrawer.drawerTitle}
        closeCta={mtaMoreInfoDrawer.closeLabel}
        content={mtaMoreInfoDrawer.content}
      />
    </div>
  );
}
